import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Disclosure } from '@headlessui/react';
import { Mail, Phone } from 'lucide-react';
import parse from 'html-react-parser';
import { getFullUrl } from '../../utils/utils';
import { formatBrandName, supportHostnameFor, lowerCaseBrandName, FOOTER_LOGOS } from '../../utils/utils';
import { getSocialLinks, SOCIAL_ICONS } from '../../utils/socialLinks';
import { useNewsletterApi } from '../../hooks/useNewsletterApi';
import { showToast } from './Toast';
import LoginModal from './LoginModal';
import shipplayLightLogo from '../../images/shipplay/shipplay_light.svg';
import shipplayFacebook from '../../images/shipplay/socials/facebook.svg';
import shipplayInstagram from '../../images/shipplay/socials/instagram.svg';
import shipplayLinkedin from '../../images/shipplay/socials/linkedin.svg';
import shipplayTikTok from '../../images/shipplay/socials/tiktok.svg';
import shipplayX from '../../images/shipplay/socials/x.svg';
import shipplayYouTube from '../../images/shipplay/socials/youtube.svg';

const FooterLinkGroup = ({ title, children }) => (
  <>
    {/* Desktop: plain column */}
    <div className="hidden lg:flex flex-col gap-4 w-full">
      <h3 className="text-white font-semibold">{title}</h3>
      <ul className="list-none flex flex-col gap-2 p-0 m-0">
        {children}
      </ul>
    </div>
    {/* Mobile: accordion */}
    <Disclosure as="div" className="lg:hidden w-full border-b border-white">
      {({ open }) => (
        <>
          <Disclosure.Button className={`
            flex w-full justify-between items-center
            py-4 px-0 text-white font-semibold text-base
            bg-transparent border-0 cursor-pointer
          `}>
            <span>{title}</span>
            <svg
              width="20" height="20" viewBox="0 0 20 20"
              fill="none" className="text-white"
            >
              {open ? (
                <path d="M4 10h12" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M10 4v12M4 10h12" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </Disclosure.Button>
          <Disclosure.Panel className="pb-4">
            <ul className="list-none flex flex-col gap-2 p-0 m-0">
              {children}
            </ul>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  </>
);

const SUPPORT_BASE_URL = 'https://support';
const SHIPPLAY_PARTNER_URL = 'https://support.shipplay.com/hc/en-us/articles/18737832414996-Becoming-a-partner';
const SHIPSTICKS_PARTNER_URL = 'https://shipsticks.com/partnership-benefits';

// Ship&Play footer link groups — mirror shipsticks-frontend's
// components/shipplay/siteLinks.ts verbatim so the PPP footer surfaces
// the same nav (labels, urls, target flags) as the Kettle site footer.
const BRAND_LINK_GROUP = {
  id: 'brand',
  links: [
    { label: 'Home', url: '/' },
    { label: 'How It Works', url: '/how-it-works' },
    { label: 'How to Pack', url: '/pack' },
    { label: 'What Can I Ship?', url: '/what-can-i-ship' },
    { label: 'Our Guarantee', url: '/guarantee' },
    { label: 'Working with an Advisor?', url: '/travel' },
    { label: 'Ship to a Cruise', url: '/cruises' },
    { label: 'Reviews', url: '/reviews' },
    { label: 'Gift Cards', url: '/gift-cards' },
    { label: 'Shipment Tracking', url: '/track-orders' },
    { label: 'Ship', url: '/book/ship', external: true },
  ],
};

const HELP_SUPPORT_LINK_GROUP = {
  id: 'help',
  title: 'Help & Support',
  links: [
    { label: 'FAQ', url: '/faq' },
    { label: 'Support', url: 'https://support.shipplay.com/', target: '_blank' },
    { label: 'Claims Center', url: '/claims' },
    { label: 'Drop-off Locator', url: '/drop-off-locator' },
    { label: 'Contact Us', url: '/contact' },
  ],
};

const LOGGED_OUT_ACCOUNT_LINK_GROUP = {
  id: 'account',
  title: 'Account',
  links: [
    // action: openLoginModal → opens shared PartnerPage/LoginModal; matches
    // the Kettle main SiteFooter behavior (login opens a modal, not a
    // hard nav).
    { action: 'openLoginModal', label: 'Login', url: '#' },
    // Create Account will be wired up alongside the header signup flow
    // in a separate ticket; leaving the label out of the footer for now
    // rather than dead-linking to /signup.
    // { label: 'Create Account', url: '/signup' },
  ],
};

const ABOUT_US_LINK_GROUP = {
  id: 'about',
  title: 'About Us',
  links: [
    { label: 'About Us', url: '/about' },
    { label: 'Careers', url: '/careers' },
  ],
};

const PARTNERS_LINK_GROUP = {
  id: 'partners',
  title: 'Our Partners',
  links: [
    { label: 'Partner with Us', url: '/partnership-benefits' },
    { label: 'Partnership Agreement & Terms', url: '/partner-agreement-terms' },
    { label: 'Become an Affiliate', url: '/affiliate' },
    { label: 'Travel Advisor Registration', url: '/advisor-signup' },
  ],
};

const FOOTER_UTILITY_LINKS = [
  { label: 'Terms of Use', url: '/terms' },
  { label: 'Privacy Policy', url: '/privacy' },
  { label: 'Support', url: 'https://support.shipplay.com/', target: '_blank' },
  { label: 'Sitemap', url: '/sitemap' },
];

// Ship&Play socials — mirrors socialLinks[] in
// shipsticks-frontend/src/components/shipplay/SiteFooter.tsx. Each entry's
// className drives per-icon size (matches playground's per-shape optical
// balancing on the size-11 anchor tile).
const SHIPPLAY_SOCIAL_LINKS = [
  {
    href: 'https://www.linkedin.com/company/ship-play/',
    src: shipplayLinkedin,
    alt: 'LinkedIn',
    className: 'w-8 h-8',
  },
  {
    href: 'https://www.facebook.com/shipandplay',
    src: shipplayFacebook,
    alt: 'Facebook',
    className: 'w-8 h-8',
  },
  {
    href: 'https://youtube.com/@shipandplay',
    src: shipplayYouTube,
    alt: 'YouTube',
    className: 'w-7 h-7',
  },
  {
    href: 'https://www.instagram.com/shipandplayofficial',
    src: shipplayInstagram,
    alt: 'Instagram',
    className: 'w-7 h-7',
  },
  {
    href: 'https://x.com/shipandplay_',
    src: shipplayX,
    alt: 'X',
    className: 'w-5 h-5',
  },
  {
    href: 'https://www.tiktok.com/@shipandplay',
    src: shipplayTikTok,
    alt: 'TikTok',
    className: 'w-6 h-6',
  },
];

// One-column arrangement stacks every group; two-column and three-column
// arrangements match SiteFooter's `getFooterLinkColumns(isLoggedIn, N)`.
const getShipplayFooterLinkColumns = (columnCount) => {
  const account = LOGGED_OUT_ACCOUNT_LINK_GROUP;

  if (columnCount === 1) {
    return [
      [BRAND_LINK_GROUP, HELP_SUPPORT_LINK_GROUP, ABOUT_US_LINK_GROUP, PARTNERS_LINK_GROUP, account],
    ];
  }
  if (columnCount === 2) {
    return [
      [BRAND_LINK_GROUP, ABOUT_US_LINK_GROUP],
      [HELP_SUPPORT_LINK_GROUP, PARTNERS_LINK_GROUP, account],
    ];
  }
  return [
    [BRAND_LINK_GROUP],
    [HELP_SUPPORT_LINK_GROUP, PARTNERS_LINK_GROUP],
    [ABOUT_US_LINK_GROUP, account],
  ];
};

// Mirrors shipsticks-frontend/src/components/shipplay/NewsletterSignUpForm.tsx:
// white pill wrapping an email input + green "Sign Up" button. Uses the
// existing shipsticks Rails newsletter endpoint (Sailthru reoptin) instead
// of Kettle's Drip form so the same audience list keeps receiving signups.
const InlineNewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { submitNewsletter } = useNewsletterApi();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!agreed) {
      showToast('Please agree to the Terms of Use and Privacy Policy.', 'error');
      return;
    }
    if (!email) {
      showToast('Please enter an email address.', 'error');
      return;
    }
    setSubmitting(true);
    const response = await submitNewsletter({ email });
    setSubmitting(false);
    if (response.success) {
      showToast('Thank you for subscribing!', 'success');
      setEmail('');
    } else {
      showToast(response.message, 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <label className="flex h-14 items-center rounded-lg bg-white p-0.5 text-base xs:max-w-80 sm:text-sm">
        <input
          type="email"
          name="email"
          required
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-full w-full pl-4 pr-2 text-base text-[#1c2938] outline-0 ring-0 sm:text-sm bg-transparent"
        />
        <button
          type="submit"
          disabled={submitting}
          className={`
            h-full rounded-md bg-[#48c44f] px-4 font-medium
            text-[#1c2938] whitespace-nowrap
            transition-colors hover:bg-[#02a40b]
            disabled:cursor-not-allowed disabled:bg-[#48c44f]/60
            active:translate-y-0 active:scale-95
          `}
        >
          Sign Up
        </button>
      </label>
      {/*
        GSE-12108 — release 26.13.03 added a consent line under the
        newsletter signup on the Kettle main SiteFooter. Mirror it here.
        Defaults to checked so the flow does not regress; unchecking
        disables the Sign Up button and blocks submit.
      */}
      <label className="mt-2 flex items-start gap-2 text-xs sm:text-[13px] cursor-pointer">
        <input
          type="checkbox"
          name="i_agree"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[#48c44f] shrink-0"
        />
        <span className="leading-snug">
          I agree to Ship&amp;Play{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noreferrer"
            className="underline decoration-[#48c44f] underline-offset-2 hover:text-[#48c44f]"
          >
            Terms of Use
          </a>
          {' '}and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline decoration-[#48c44f] underline-offset-2 hover:text-[#48c44f]"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>
    </form>
  );
};

// snplay-footer-link → matches globals.css `.snplay-footer-link` @apply chain.
const FOOTER_LINK_CLASS = `
  inline-block cursor-pointer py-2.5 text-base font-medium leading-6
  transition-colors hover:text-[#48c44f] md:py-1
`;
// snplay-group-name (with data-bg=dark override) → text-primary (#48c44f).
const GROUP_NAME_CLASS = `
  mb-2 inline-block text-xs font-semibold uppercase text-[#48c44f]
`;

const ShipplayFooterLinkGroup = ({ brandName, group, onAction }) => (
  <section>
    <span className={GROUP_NAME_CLASS}>
      {group.id === 'brand' ? brandName : group.title}
    </span>
    <ul className="space-y-1 list-none p-0 m-0">
      {group.links.map((link) => {
        const isExternal = link.external || /^https?:\/\//.test(link.url);
        return (
          <li key={link.label}>
            <a
              href={link.url}
              target={link.target}
              rel={isExternal ? 'noreferrer' : undefined}
              className={FOOTER_LINK_CLASS}
              onClick={(e) => {
                if (link.action) {
                  e.preventDefault();
                  onAction?.(link.action);
                }
              }}
            >
              {link.label}
            </a>
          </li>
        );
      })}
    </ul>
  </section>
);

ShipplayFooterLinkGroup.propTypes = {
  brandName: PropTypes.string.isRequired,
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    links: PropTypes.array.isRequired,
  }).isRequired,
  onAction: PropTypes.func,
};

const renderFooterLinkColumns = (brandName, columns, onAction) =>
  columns.map((column) => (
    <div className="space-y-8" key={column.map((g) => g.id).join('-')}>
      {column.map((group) => (
        <ShipplayFooterLinkGroup
          key={group.id}
          brandName={brandName}
          group={group}
          onAction={onAction}
        />
      ))}
    </div>
  ));

const ShipPlayFooter = ({
  subdomain, supportPhoneNumber, brandPhoneNumber, showNewsletter,
}) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const handleAction = (action) => {
    if (action === 'openLoginModal') setIsLoginModalOpen(true);
  };
  const brandName = 'Ship&Play';
  const logoUrl = shipplayLightLogo;
  const email = { display: 'info@shipplay.com', href: 'mailto:info@shipplay.com' };
  const address = ['11400 N. Jog Road, Suite 300', 'Palm Beach Gardens, Florida 33418'];

  const rawPhone = (supportPhoneNumber === null || supportPhoneNumber === '')
    ? brandPhoneNumber
    : supportPhoneNumber;
  const digits = (rawPhone || '').replace(/\D/g, '');
  const displayPhone = digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : (rawPhone || '(855) 792-7529');
  const phone = {
    display: displayPhone,
    href: `tel:+1${(displayPhone || '').replace(/\D/g, '')}`,
  };

  const oneColumnLinkColumns = getShipplayFooterLinkColumns(1);
  const twoColumnLinkColumns = getShipplayFooterLinkColumns(2);
  const threeColumnLinkColumns = getShipplayFooterLinkColumns(3);

  // Copyright renders the canonical no-spaces form "Ship&Play" — the
  // spaced-form carve-out was removed from the Kettle SiteFooter in
  // release 26.13.03 (GSE-12184), so the PPP mirrors that.
  const copyrightText = `© Copyright ${new Date().getFullYear()} Ship&Play.`;

  return (
    <div className="bg-[#1c2938] text-white font-poppins">
      <footer className="mx-auto max-w-[1696px] px-6 md:px-12 lg:px-16 xl:px-32">
        <span className="mb-24 block h-2 w-full bg-[#48c44f]">&nbsp;</span>

        <div className="grid-cols-12 gap-8 md:grid">
          <div className="col-span-5 lg:col-span-4">
            <a href={getFullUrl('/', subdomain)} className="mb-6 inline-block">
              <img src={logoUrl} className="max-h-10" alt={brandName} />
            </a>
            <p className="mb-4 text-base sm:text-sm">
              {address.map((line, i) => (
                <React.Fragment key={line}>
                  {line}
                  {i < address.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <div className="mb-8 -ml-2 flex flex-wrap items-center gap-1">
              {SHIPPLAY_SOCIAL_LINKS.map((social) => (
                <a
                  key={social.alt}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.alt}
                  className="flex w-11 h-11 items-center justify-center"
                >
                  <img src={social.src} alt={social.alt} className={social.className} />
                </a>
              ))}
            </div>
            {/*
              Was `sm:flex md:block` in playground (sm=640 there = tablets
              only). Rails Tailwind's sm=640 wouldn't repro the phone-only
              bug Kettle hit, but we mirror the vertical stack for parity.
            */}
            <ul className="mb-12 gap-12 space-y-2 list-none p-0">
              <li>
                <a
                  href={email.href}
                  className={`${FOOTER_LINK_CLASS} inline-flex items-center gap-4`}
                >
                  <Mail className="w-6 h-6 shrink-0 text-[#48c44f]" />
                  {email.display}
                </a>
              </li>
              <li>
                <a
                  href={phone.href}
                  className={`${FOOTER_LINK_CLASS} inline-flex items-center gap-4 whitespace-nowrap`}
                >
                  <Phone className="w-6 h-6 shrink-0 text-[#48c44f]" />{' '}
                  {phone.display}
                </a>
              </li>
            </ul>
            {showNewsletter && (
              <div>
                <span className={`${GROUP_NAME_CLASS} mb-1 block text-[#48c44f]`}>
                  Newsletter
                </span>
                <p className="mb-6 text-balance text-base sm:text-sm">
                  Sign up for our mailing list to get the latest updates and special offers.
                </p>
                <InlineNewsletterForm />
              </div>
            )}
          </div>
          <div
            className={`
              group col-span-6 col-start-7
              pt-16 max-xs:space-y-8
              lg:col-span-7 lg:col-start-6
            `}
            data-bg="dark"
          >
            <div className="space-y-8 xs:hidden">
              {renderFooterLinkColumns(brandName, oneColumnLinkColumns, handleAction)}
            </div>
            <div className="hidden gap-8 xs:grid xs:grid-cols-2 lg:hidden">
              {renderFooterLinkColumns(brandName, twoColumnLinkColumns, handleAction)}
            </div>
            <div className="hidden gap-8 lg:grid lg:grid-cols-3">
              {renderFooterLinkColumns(brandName, threeColumnLinkColumns, handleAction)}
            </div>
          </div>
        </div>

        <div
          className={`
            mt-24 items-center justify-between gap-8
            border-t border-[#30465F] py-4
            text-base max-sm:text-center sm:text-sm lg:flex
          `}
        >
          <div className="text-balance max-lg:my-4 max-lg:text-center">
            <span className="inline-block">{copyrightText}</span>{' '}
            <span className="inline-block">All rights reserved.</span>
          </div>
          <div className="flex flex-wrap items-center justify-center lg:justify-end">
            {FOOTER_UTILITY_LINKS.map((link) => (
              <React.Fragment key={link.label}>
                <a
                  href={link.url}
                  target={link.target}
                  rel={/^https?:\/\//.test(link.url) ? 'noreferrer' : undefined}
                  className={`${FOOTER_LINK_CLASS} whitespace-nowrap text-base sm:text-sm`}
                >
                  {link.label}
                </a>
                <span className="mx-3 text-white/[.44]">&bull;</span>
              </React.Fragment>
            ))}
            <span className="whitespace-nowrap text-[#48c44f]">Version 2.0</span>
          </div>
        </div>
      </footer>
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        sourceType="shipplay-footer"
      />
    </div>
  );
};

ShipPlayFooter.propTypes = {
  subdomain: PropTypes.string,
  supportPhoneNumber: PropTypes.string,
  brandPhoneNumber: PropTypes.string,
  showNewsletter: PropTypes.bool,
};

const Footer = ({
  bgColor = '#1C2938', header, body, children, brand, shipLinkPath, subdomain,
  supportPhoneNumber, brandPhoneNumber, showNewsletter = false,
}) => {
  // Ship&Play PPP footer mirrors the Kettle main SiteFooter (see
  // shipsticks-frontend/src/components/shipplay/SiteFooter.tsx). Other
  // brands keep the legacy layout below.
  //
  // Different callers pass different brand forms:
  //   - PremiumPartnerPage(2) → partnerConfig.brand ("shipplay" — slug)
  //   - PartnerPages/Page → partnerData.brand.name ("Ship & Play" — display)
  // Normalize (strip spaces + non-alphanumerics, lowercase) so both hit the
  // shipplay branch. Non-shipplay brands ("shipsticks", "shipskis", "Ship
  // Sticks", …) never collapse to "shipplay", so the legacy path stays intact.
  const normalizedBrand = String(brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalizedBrand === 'shipplay') {
    // ShipPlayFooter deliberately drops `children` / `header` / `body`
    // (concierge blurb, T&C, standalone <Newsletter />) — the Kettle
    // main SiteFooter has no such content, and rendering it above the
    // grid would break the parity. Newsletter opts into inline via
    // `showNewsletter`. Callers can keep passing the same props/children
    // for non-shipplay brands; they simply do not apply here.
    return (
      <ShipPlayFooter
        subdomain={subdomain}
        supportPhoneNumber={supportPhoneNumber}
        brandPhoneNumber={brandPhoneNumber}
        showNewsletter={showNewsletter}
      />
    );
  }

  const rawPhoneNumber = (supportPhoneNumber === null || supportPhoneNumber === '')
    ? brandPhoneNumber
    : supportPhoneNumber;

  const formatPhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const displayPhoneNumber = formatPhone(rawPhoneNumber);

  function getSupportLink(path) {
    if (path === '/partners') {
      return brand === 'shipplay' ? SHIPPLAY_PARTNER_URL : SHIPSTICKS_PARTNER_URL;
    }

    const brandSlug = brand.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return `${SUPPORT_BASE_URL}.${brandSlug}.com/hc/en-us`;
  }

  useEffect(() => {
    console.log('Current brand:', brand);
    console.log('Brand name:', formatBrandName(brand));
    console.log('Lower case brand name:', lowerCaseBrandName(brand));
    console.log('Resolved logo:', FOOTER_LOGOS[lowerCaseBrandName(brand)]);
  }, []);

  const resolvedLogo = FOOTER_LOGOS[lowerCaseBrandName(brand)];
  const brandName = formatBrandName(brand);
  const supportHostname = supportHostnameFor(brand);
  const linkClass = 'text-white text-sm hover:text-white';
  const legalLinkClass = 'text-white text-xs hover:text-white';

  return (
    <footer
      className="text-white px-6 py-8 sm:p-8 lg:px-14 xl:px-36 2xl:px-56 font-poppins"
      style={{ backgroundColor: bgColor }}
    >
      <div className="max-w-7xl mx-auto">
        {header && body && (
          <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
            <h5 className="text-white text-xl font-semibold mb-2">{parse(header)}</h5>
            <p className="mb-2 text-lg leading-8">{parse(body)}</p>
          </div>
        )}
        {children}
        {/* Logo + Social Links */}
        <div className={`
          flex flex-col lg:flex-row justify-between items-center
          gap-x-12 gap-y-6 mb-12 py-8
          ${header && body ? 'border-t border-[#FFF] border-opacity-20' : ''}
        `}>
          <a href={getFullUrl('/', subdomain)}>
            <img
              src={resolvedLogo}
              alt="Footer Logo"
              className="w-[14rem] xl:w-[261px]"
            />
          </a>
          <ul className="list-none flex items-center gap-6 p-0 m-0">
            {Object.entries(SOCIAL_ICONS).map(([key, { label, path }]) => {
              const href = getSocialLinks(brand)[key];
              if (!href) return null;
              return (
                <li key={key}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="text-white hover:text-white"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d={path} />
                    </svg>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Primary Links */}
        <div className={`
          flex flex-col lg:flex-row justify-between items-start
          gap-x-[67px] gap-y-2 w-full lg:pb-6 lg:border-b
          border-neutral-400 mb-10 md:mb-12 lg:mb-10
        `}>
          <FooterLinkGroup title={brandName}>
            <li><a href={getFullUrl('/', subdomain)} className={linkClass}>Home</a></li>
            <li><a href={getFullUrl(shipLinkPath, subdomain)} className={linkClass}>Ship</a></li>
            <li><a href={getFullUrl('/track', subdomain)} className={linkClass}>Track</a></li>
            <li><a href={getFullUrl('/gift-cards', subdomain)} className={linkClass}>Gift Cards</a></li>
          </FooterLinkGroup>

          <FooterLinkGroup title="Help">
            <li><a href={getFullUrl('/how-it-works', subdomain)} className={linkClass}>How it works</a></li>
            <li>
              <a href={getSupportLink('/support')} target="_blank" rel="noreferrer" className={linkClass}>
                Support
              </a>
            </li>
            <li><a href={getFullUrl('/claims', subdomain)} className={linkClass}>Claims</a></li>
            <li><a href={getFullUrl('/store_locator', subdomain)} className={linkClass}>Drop-off locator</a></li>
          </FooterLinkGroup>

          <FooterLinkGroup title="Partner with us">
            <li>
              <a href={getSupportLink('/partners')} target="_blank" rel="noreferrer" className={linkClass}>
                Become a partner
              </a>
            </li>
          </FooterLinkGroup>

          <FooterLinkGroup title="About us">
            <li><a href={getFullUrl('/about', subdomain)} className={linkClass}>About Us</a></li>
            <li><a href={getFullUrl('/contact', subdomain)} className={linkClass}>Contact Us</a></li>
          </FooterLinkGroup>

          <FooterLinkGroup title="Get in touch">
            <li className="text-white text-sm">
              11400 N. Jog Rd, Suite 300<br />
              Palm Beach Gardens<br />Florida 33418
            </li>
            <li>
              <a className={linkClass} href={`tel:${(displayPhoneNumber || '').replace(/\D/g, '')}`}>
                {displayPhoneNumber}
              </a>
            </li>
            <li>
              <a className={linkClass} href={`mailto:info@${supportHostname}`}>
                {`info@${supportHostname}`}
              </a>
            </li>
          </FooterLinkGroup>
        </div>

        {/* Secondary Links */}
        <div className="flex md:justify-center lg:justify-start flex-col md:flex-row md:gap-x-6 lg:gap-x-12 gap-y-4">
          <a href={getFullUrl('/terms', subdomain)} className={legalLinkClass}>Terms of Use</a>
          <a href={getFullUrl('/privacy', subdomain)} className={legalLinkClass}>Privacy Policy</a>
          <a href={getFullUrl('/cookie-policy', subdomain)} className={legalLinkClass}>Cookie Policy</a>
          <a href={getFullUrl('/usage-agreement', subdomain)} className={legalLinkClass}>Usage Right</a>
          <a href={getSupportLink('/support')} target="_blank" rel="noreferrer" className={legalLinkClass}>Support</a>
          <a href={getFullUrl('/sitemap', subdomain)} className={legalLinkClass}>Site Map</a>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  header: PropTypes.string,
  body: PropTypes.string,
  bgColor: PropTypes.string,
  brand: PropTypes.string,
  children: PropTypes.node,
  shipLinkPath: PropTypes.string.isRequired,
  subdomain: PropTypes.string,
  supportPhoneNumber: PropTypes.string,
  brandPhoneNumber: PropTypes.string,
  showNewsletter: PropTypes.bool,
};

export default Footer;
